/**
 * inventoryAlertsService.js
 * All calls the Inventory Alerts screens make to the Django backend.
 *
 * If your app already has an axios instance that adds the login header, import it here and
 * replace `client` below - nothing else in the components needs to change.
 */
import axios from "axios";
import { CURRENT_API_URL } from "../../../Utils/Constants";

const API_ROOT =
  CURRENT_API_URL ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL) ||
  "https://api.app.simpliworks.io";

const client = axios.create({ baseURL: `${API_ROOT}/sellerPartner`, timeout: 60000 });

// Same login header as the rest of the app.
client.interceptors.request.use((config) => {
  let token = localStorage.getItem("token");
  if (!token && typeof window !== "undefined" && window.store) {
    try {
      const state = window.store.getState();
      token = state && state.loggedUser && state.loggedUser.token;
    } catch (e) {
      // ignore
    }
  }
  if (token) {
    config.headers.Authorization = token.startsWith("Token ") ? token : `Token ${token}`;
  }
  return config;
});

function unwrap(promise) {
  return promise
    .then((res) => res.data)
    .catch((err) => {
      const body = err.response && err.response.data;
      const message = body && body.error ? `${body.error}${body.details ? `: ${body.details}` : ""}` : err.message;
      const e = new Error(message);
      e.status = err.response ? err.response.status : null;
      e.body = body;
      throw e;
    });
}

/** Everything on the screens for one store. sku -> adds skuDetail; settings -> overrides for this view. */
export function getInventoryAlerts({ profileId, sections, sku, settings }) {
  if (settings) {
    return unwrap(client.post("/inventory-alerts", { profileId, sections, sku, settings }));
  }
  const params = { profileId };
  if (sections && sections.length) params.sections = sections.join(",");
  if (sku) params.sku = sku;
  return unwrap(client.get("/inventory-alerts", { params }));
}

/** Drawer data for one SKU (small request). */
export function getSkuDetail({ profileId, sku, settings }) {
  return getInventoryAlerts({ profileId, sku, settings, sections: ["inventoryHealth"] }).then((d) => d.skuDetail);
}

/** Internal: system health for every managed store. */
export function getAllStoresHealth() {
  return unwrap(client.get("/inventory-alerts", { params: { allStores: true } })).then((d) => d.systemHealth);
}

/** Queue the scheduled fetch job (reports + inventory APIs) for this store. */
export function submitFetchJob({ profileId, forceReports = false }) {
  return unwrap(client.post("/inventory-alerts/refresh", { profileId, forceReports }));
}

/** Progress of the store's fetch job. */
export function getFetchJobStatus({ profileId, jobId }) {
  const params = { profileId };
  if (jobId) params.jobId = jobId;
  return unwrap(client.get("/inventory-alerts/refresh-status", { params }));
}
